// stores/masterVehicleStore.js
'use client';
import { fleetApi } from '../api/axiosClient';
import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { normalizeApiResponse } from '../utils/response';
import { apiOslogCrud } from 'helpers/oslogFormatEndpoint';
import { useMemo, useState, useEffect } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const getCompanyId = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('companyId');
      if (stored && String(stored).toLowerCase() !== 'all') {
        const n = Number(stored);
        if (Number.isFinite(n)) return n;
      }
    }
  } catch (err) {
    console.warn('getCompanyId: cannot read localStorage', err);
  }
  return 0;
};

export const revalidateMasterVehicleList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/vehicle/search');
};

const vehicleApi = apiOslogCrud('vehicle');
const fetcher = async ([url, payloadStr]) => {
  const payload = JSON.parse(payloadStr);
  const response = await vehicleApi.search(payload);
  return response;
};

export const useMasterVehicleList = (page, rowsPerPage, selectedCompanies = [], searchKey, searchValue) => {
  const isFetchAll = page === undefined;
  const companies = (selectedCompanies || []).map((c) => c.value).join(',');

  const start = isFetchAll ? 0 : page * rowsPerPage;

  const tableName =
    searchKey === 'vendor' ? 'm_vendor' : searchKey === 'pool' ? 'm_pool' : searchKey === 'vehicle_type' ? 'm_vehicle_type' : null;
  const filterLike = searchKey === 'vendor' || searchKey === 'pool' || searchKey === 'vehicle_type' ? 'name' : searchKey;

  const payload = useMemo(() => {
    if (isFetchAll) return null; // We'll handle isFetchAll manually
    const builder = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .addJoin('company', ['name'])
      .addJoin('vendor', ['name'])
      .addJoin('pool', ['name'])
      .addJoin('vehicle_type', ['name'])
      .setOrder(['license_plate'], true);

    if (companies) {
      builder.in('company_id', companies);
    }

    if (searchValue && searchValue.trim() !== '') {
      builder.like(filterLike, searchValue, 'AND', tableName);
    }

    return builder.build();
  }, [isFetchAll, start, rowsPerPage, companies, searchValue, filterLike, tableName]);

  const key = useMemo(() => {
    if (isFetchAll) return null; // Disable SWR when fetching all
    return ['/vehicle/search', JSON.stringify(payload)];
  }, [isFetchAll, payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  // State untuk manual fetch ALL data (ketika page === undefined)
  const [allData, setAllData] = useState([]);
  const [isAllLoading, setIsAllLoading] = useState(isFetchAll);
  const [allError, setAllError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchAllVehicles = async () => {
      setIsAllLoading(true);
      setAllError(null);
      try {
        let allVehicles = [];
        let currentStart = 0;
        let currentLength = 100;
        let hasMore = true;

        while (hasMore && isMounted) {
          const builder = new FilterBuilder()
            .setPaging(currentStart, currentLength)
            .addJoin('company', ['name'])
            .addJoin('vendor', ['name'])
            .addJoin('pool', ['name'])
            .addJoin('vehicle_type', ['name'])
            .setOrder(['license_plate'], true);

          if (companies) {
            builder.in('company_id', companies);
          }

          const currentPayload = builder.build();
          const res = await vehicleApi.search(currentPayload);
          const raw = res?.data?.data ?? res?.data ?? [];

          allVehicles = allVehicles.concat(raw);

          if (raw.length < currentLength) {
            hasMore = false;
          } else {
            currentStart += currentLength;
          }
        }

        if (isMounted) setAllData(allVehicles);
      } catch (err) {
        if (isMounted) setAllError(err);
      } finally {
        if (isMounted) setIsAllLoading(false);
      }
    };

    if (isFetchAll) {
      fetchAllVehicles();
    }

    return () => {
      isMounted = false;
    };
  }, [isFetchAll, companies]);

  if (isFetchAll) {
    return {
      data: allData,
      totalRecord: allData.length,
      isLoading: isAllLoading,
      isError: allError
    };
  }

  const result = normalizeApiResponse(data, error, isLoading);
  return result;
};

export const getVehicleById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).equal('id', String(id));

    const payload = fb.build();
    const response = await fleetApi.post(`/vehicle/search${getTzParam()}`, payload);

    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);

    const result = Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
    return result;
  } catch (error) {
    console.error('Failed to fetch vehicle by ID:', error);
    throw error;
  }
};

export const createVehicle = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.post(`/vehicle/new${getTzParam()}`, body);

    if (
      response?.data?.error === true ||
      response?.data?.code >= 400 ||
      response?.data?.code >= 403 ||
      (response?.data?.status === false && response?.data?.message)
    ) {
      throw { response: { data: response.data } };
    }
    revalidateMasterVehicleList();
    return response.data;
  } catch (error) {
    console.error('Error creating vehicle:', error?.response?.data ?? error);
    throw error;
  }
};

export const updateVehicle = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      company: formData.company ?? getCompanyId(),
      ...formData
    };

    const response = await fleetApi.put(`/vehicle/edit/${id}${getTzParam()}`, body);

    if (
      response?.data?.error === true ||
      response?.data?.code >= 400 ||
      response?.data?.code >= 403 ||
      (response?.data?.status === false && response?.data?.message)
    ) {
      throw { response: { data: response.data } };
    }
    revalidateMasterVehicleList();
    return response.data;
  } catch (error) {
    console.error('Error updating vehicle:', error?.response?.data ?? error);
    throw error;
  }
};

export const deleteVehicle = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/vehicle/delete/${id}${getTzParam()}`);

    if (
      response?.data?.error === true ||
      response?.data?.code >= 400 ||
      response?.data?.code >= 403 ||
      (response?.data?.status === false && response?.data?.message)
    ) {
      throw { response: { data: response.data } };
    }
    revalidateMasterVehicleList();
    return response.data;
  } catch (error) {
    console.error('Error deleting vehicle:', error?.response?.data ?? error);
    throw error;
  }
};

export const uploadVehicleBarcodeImage = async (id, file) => {
  if (id == null) throw new Error('id is required');
  if (!file) throw new Error('file is required');
  try {
    const fd = new FormData();
    fd.append('file', file);
    const response = await fleetApi.post(`/vehicle/barcode/uploadfile/${id}${getTzParam()}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading vehicle barcode image:', error?.response?.data ?? error);
    throw error;
  }
};

export const getVehicleImage = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.get(`/vehicle/images/${id}${getTzParam()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching vehicle image:', error?.response?.data ?? error);
    throw error;
  }
};
export const getVehicleBarcodeImage = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.get(`vehicle/barcode/images/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching vehicle barcode image:', error?.response?.data ?? error);
    throw error;
  }
};

export const deleteVehicleImage = async (id, fileName) => {
  if (id == null) throw new Error('id is required');
  if (!fileName) throw new Error('fileName is required');
  try {
    const response = await fleetApi.delete(`/vehicle/images/${id}?file=${fileName}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting vehicle image:', error?.response?.data ?? error);
    throw error;
  }
};
export const deleteVehicleBarcodeImage = async (id, fileName) => {
  if (id == null) throw new Error('id is required');
  if (!fileName) throw new Error('fileName is required');
  try {
    const response = await fleetApi.delete(`/vehicle/barcode/images/${id}?file=${fileName}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting vehicle barcode image:', error?.response?.data ?? error);
    throw error;
  }
};

export const uploadVehicleImage = async (id, file) => {
  if (id == null) throw new Error('id is required');
  if (!file) throw new Error('file is required');
  try {
    const fd = new FormData();
    fd.append('file', file);
    const response = await fleetApi.post(`/vehicle/uploadfile/${id}${getTzParam()}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading vehicle image:', error?.response?.data ?? error);
    throw error;
  }
};
